import { cloneDeep } from 'lodash'
import { getProductListData, _getPRDataById } from '../products/mockData'

import { getSupplierListData, _getSDataById } from '../suppliers/mockData'
import {
  getSupplierProductItemsDataForSupplierProduct,
  getRootLevelSupplierProductItemsCountForSupplierProduct,
} from '../supplierProductItems/mockData'
import { getSupplierProductDocumentListDataBySupplierProductId } from '../supplierProductDocuments/mockData'
import { getSupplierProductNoteListDataBySupplierProductId } from '../supplierProductNotes/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import type { TProduct } from '~/types/product/models'
import type { ISupplier } from '~/types/supplier/models'
import type { ISupplierProductItem } from '@/types/supplierProductItem/models'
import type {
  ISupplierProductOverview,
  ISupplierProduct,
  ISupplierProductWithItemsPropertyPopulated,
} from '~/types/supplierProduct/models'
import { TEntityId } from '~/types/models'
import {
  SupplierProductStatusIds,
  getSupplierProductStatusById,
} from '~/services/selectLists/supplierProductStatii'

const mn = '[api/msw/api/modules/supplierProducts/mockdData.ts]'
const supplierProductListData = undefined as unknown as ISupplierProduct[]

export const getSupplierProductListData = getListDataFunctionFactory<
  ISupplierProduct[]
>({
  listData: supplierProductListData,
  storageKey: 'supplierproducts',
  getStartingListData: getStartingSupplierProductListData,
  forOfFunction: async (o) => {
    o.numItems = await getRootLevelSupplierProductItemsCountForSupplierProduct(
      o.id
    )
  },
  doSkipForEachFunction({
    didUseEntityListDataFromStorage,
  }: {
    didUseEntityListDataFromStorage: boolean
  }) {
    return !didUseEntityListDataFromStorage
  },
})

export async function getSupplierProductData(
  id: string
): Promise<ISupplierProduct> {
  const list: ISupplierProduct[] = await getSupplierProductListData()
  if (!list) {
    throw new Error(`supplierProductList returned undefined/null`)
  }
  const supplierProduct = list.find((sp) => sp.id === id)
  if (!supplierProduct) {
    throw new Error(`Can't find supplierProduct '${id}'`)
  }
  return supplierProduct
}

export async function _getSPDataById(
  id: TEntityId,
  list: ISupplierProduct[]
): Promise<ISupplierProduct | undefined> {
  if (!list) {
    throw new Error(`_getSPDataById called with undefined/null list`)
  }
  const data = list.find((supplierProduct) => supplierProduct.id === id)
  return data
}

const DO_NOT_RECURSE = true
const RECURSE = !DO_NOT_RECURSE

export async function getSupplierProductOverviewData(id: string) {
  const supplierProductData = (await getSupplierProductData(
    id
  )) as ISupplierProduct
  if (!supplierProductData) {
    throw new Error(`SupplierProduct data for supplierProduct ${id} not found`)
  }
  const supplierProductItemsData =
    (await getSupplierProductItemsDataForSupplierProduct({
      id,
      doRecurse: false,
      doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem: false,
    })) as ISupplierProductItem[]

  const overviewData: ISupplierProductOverview = {
    supplierProduct: supplierProductData,
    supplierProductItems: supplierProductItemsData,
    supplierProductDocuments:
      (await getSupplierProductDocumentListDataBySupplierProductId(id)) || [],
    supplierProductNotes:
      (await getSupplierProductNoteListDataBySupplierProductId(id)) || [],
  }
  return overviewData
}

export async function getSupplierProductWithSupplierProductItemsData({
  id,
  doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem,
}: {
  id: string
  doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem: boolean
}): Promise<ISupplierProductWithItemsPropertyPopulated> {
  // Typing needs work; JR
  const fn = 'getSupplierProductWithSupplierProductItemsData |'
  console.debug(
    `${fn} called with supplierProductId: ${id},` +
      ` doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem:` +
      ` ${doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem}`
  )
  const supplierProductData = cloneDeep(await getSupplierProductData(id))
  if (!supplierProductData) {
    throw new Error('supplierProductData is falsey. This should never happen.')
  }
  console.debug(`${fn} supplierProductData:`, supplierProductData)
  supplierProductData.items =
    (await getSupplierProductItemsDataForSupplierProduct({
      id,
      doRecurse: true,
      doReplaceSupplierProductInSupplierProductItemsWithEntitySelectItem,
    })) as ISupplierProductItem[]

  return supplierProductData as ISupplierProductWithItemsPropertyPopulated
}

export async function updateSupplierInSupplierProducts({
  targetItem: supplier,
}: {
  targetItem: ISupplier
}) {
  const fn = `${mn} updateSupplierInSupplierProducts |`
  const list: ISupplierProduct[] = await getSupplierProductListData()
  if (!list) {
    throw new Error(`supplierProductList returned undefined/null`)
  }
  for (const sp of list) {
    if (sp.supplier.id === supplier.id) {
      sp.supplier = cloneDeep(supplier)
    }
  }
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'supplierproducts',
    listData: list,
    callingFn: fn,
  })
}

export async function updateProductInSupplierProducts({
  targetItem: product,
}: {
  targetItem: TProduct
}) {
  const fn = `${mn} updateProductInSupplierProducts |`
  const list: ISupplierProduct[] = await getSupplierProductListData()
  if (!list) {
    throw new Error(`supplierProductList returned undefined/null`)
  }
  for (const sp of list) {
    if (sp.product.id === product.id) {
      sp.product = cloneDeep(product)
    }
  }
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'supplierproducts',
    listData: list,
    callingFn: fn,
  })
}

export async function getStartingSupplierProductListData(): Promise<
  ISupplierProduct[]
> {
  const stListData = await getProductListData()

  const pr0006 = await _getPRDataById('XXPR0006', stListData, false) // POTS
  const pr0009 = await _getPRDataById('XXPR0009', stListData, false) // Consulting

  const pr0013 = await _getPRDataById('XXPR0013', stListData, false)

  const pr0016 = await _getPRDataById('XXPR0016', stListData, false)
  const pr0017 = await _getPRDataById('XXPR0017', stListData, false)
  const pr0018 = await _getPRDataById('XXPR0018', stListData, false)
  const pr0019 = await _getPRDataById('XXPR0019', stListData, false)
  // Asserted non-null at the declaration, unlike its neighbours: without it
  // `product:` and the descr template each raise a possibly-undefined error.
  const pr0064 = (await _getPRDataById('XXPR0064', stListData, false))! // SDWAN Device (Set)
  const pr0065 = (await _getPRDataById('XXPR0065', stListData, false))! // Managed Router (Set)
  const pr0068 = (await _getPRDataById('XXPR0068', stListData, false))! // Branch Office (Kit)
  const pr0069 = (await _getPRDataById('XXPR0069', stListData, false))! // Wi-Fi (Kit)
  const pr0070 = (await _getPRDataById('XXPR0070', stListData, false))! // Wi-Fi Mounting (Kit)
  const pr0074 = (await _getPRDataById('XXPR0074', stListData, false))! // Zscaler DLP - 3 Year (Kit)
  const pr0075 = (await _getPRDataById('XXPR0075', stListData, false))! // Data Loss Protection (DLP) (Kit)
  const pr0076 = (await _getPRDataById('XXPR0076', stListData, false))! // DLP Licensing (Set)
  const pr0077 = (await _getPRDataById('XXPR0077', stListData, false))! // SASE Support & Implementation (Set)

  const pr0025 = await _getPRDataById('XXPR0025', stListData, false) // UCaaS - Kit
  const pr0026 = await _getPRDataById('XXPR0026', stListData, false) // UCaaS Standard License
  const pr0032 = await _getPRDataById('XXPR0032', stListData, false) // TAM - Kit
  const pr0033 = await _getPRDataById('XXPR0033', stListData, false) // Toll-Free Number
  const pr0034 = await _getPRDataById('XXPR0034', stListData, false) // UCaaS Digital Unlimited Line Advanced

  const pr0035 = await _getPRDataById('XXPR0035', stListData, false) // DIA (Kit)
  const pr0036 = await _getPRDataById('XXPR0036', stListData, false) // DIA Bandwidth
  const pr0050 = await _getPRDataById('XXPR0050', stListData, false) // DIA Access
  const pr0054 = await _getPRDataById('XXPR0054', stListData, false) // IP Addresses
  const pr0060 = await _getPRDataById('XXPR0060', stListData, false) // DIA - CPE

  if (
    !pr0006 ||
    !pr0009 ||
    !pr0013 ||
    !pr0016 ||
    !pr0017 ||
    !pr0018 ||
    !pr0019 ||
    !pr0025 ||
    !pr0026 ||
    !pr0032 ||
    !pr0033 ||
    !pr0034 ||
    !pr0035 ||
    !pr0036 ||
    !pr0050 ||
    !pr0054 ||
    !pr0060
  ) {
    throw new Error('should never happen')
  }

  const sList = await getSupplierListData()

  const s0001 = _getSDataById('XXS0001', sList) // WXTG
  const s0002 = _getSDataById('XXS0002', sList) // Cogent
  const s0003 = _getSDataById('XXS0003', sList) // Comcast
  const s0005 = _getSDataById('XXS0005', sList) // RingCentral
  const s0009 = _getSDataById('XXS0009', sList) // Pac-Tel
  const s0010 = _getSDataById('XXS0010', sList) // Coeo
  const s0011 = _getSDataById('XXS0011', sList) // AppDirect

  if (!s0001 || !s0002 || !s0003 || !s0005 || !s0009 || !s0010 || !s0011) {
    throw new Error('should never happen')
  }

  return [
    {
      id: 'XXSP0003',
      hrid: 'SP0003',
      supplier: s0002,
      product: pr0035, // DIA (Kit)
      descr: `${s0002.descr} ${pr0035.descr}`,
      numItems: 3,
      numOpportunities: 120,
      numOrders: 100,
      numActiveServices: 100,
      seqNum: 1,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0004',
      hrid: 'SP0004',
      supplier: s0002,
      product: pr0013, // E-LAN (Kit)
      descr: `${s0002.descr} ${pr0013.descr}`,
      numItems: 3,
      numOpportunities: 120,
      numOrders: 100,
      numActiveServices: 100,
      seqNum: 2,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0005',
      hrid: 'SP0005',
      supplier: s0003,
      product: pr0035, // DIA (Kit)
      descr: `${s0003.descr} ${pr0035.descr}`,
      numItems: 3,
      numOpportunities: 120,
      numOrders: 100,
      numActiveServices: 100,
      seqNum: 1,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0007',
      hrid: 'SP0007',
      supplier: s0002,
      product: pr0016, // E-LAN HUB (Kit)
      descr: `${s0002.descr} ${pr0016.descr}`,
      numItems: 2,
      numOpportunities: 49,
      numOrders: 46,
      numActiveServices: 80,
      seqNum: 3,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0008',
      hrid: 'SP0008',
      supplier: s0002,
      product: pr0017, // E-LAN ENDPOINT
      descr: `${s0002.descr} ${pr0017.descr}`,
      numItems: 2,
      numOpportunities: 49,
      numOrders: 46,
      numActiveServices: 80,
      seqNum: 4,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0009',
      hrid: 'SP0009',
      supplier: s0002,
      product: pr0018, // E-LAN CIRCUIT
      descr: `${s0002.descr} ${pr0018.descr}`,
      numItems: 1,
      numOpportunities: 23,
      numOrders: 20,
      numActiveServices: 20,
      seqNum: 5,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0010',
      hrid: 'SP0010',
      supplier: s0010,
      product: pr0019, // SDWAN (Kit)
      descr: `${s0010.descr} ${pr0019.descr}`,
      // Consulting Services, License, and the SDWAN Device Set (XXSPI0057).
      numItems: 3,
      numOpportunities: 23,
      numOrders: 20,
      numActiveServices: 20,
      seqNum: 1,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0011',
      hrid: 'SP0011',
      supplier: s0005, // RingCentral
      product: pr0026, // UCaaS Standard License
      descr: `${s0005.descr} ${pr0026.descr}`,
      numItems: 0,
      numOpportunities: 19,
      numOrders: 16,
      numActiveServices: 16,
      seqNum: 1,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0012',
      hrid: 'SP0012',
      supplier: s0005, // RingCentral
      product: pr0025, // UCaaS (Kit)
      descr: `${s0005.descr} ${pr0025.descr}`,
      numItems: 4,
      numOpportunities: 19,
      numOrders: 16,
      numActiveServices: 16,
      seqNum: 2,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0013',
      hrid: 'SP0013',
      supplier: s0005, // RingCentral
      product: pr0032, // TAM (Set)
      descr: `${s0005.descr} ${pr0032.descr}`,
      numItems: 4,
      numOpportunities: 19,
      numOrders: 16,
      numActiveServices: 16,
      seqNum: 3,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0014',
      hrid: 'SP0014',
      supplier: s0001, // WXTG
      product: pr0009, // Consulting Services
      descr: `${s0001.descr} ${pr0009.descr}`,
      numItems: 1,
      numOpportunities: 53,
      numOrders: 50,
      numActiveServices: 50,
      seqNum: 1,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0015',
      hrid: 'SP0015',
      supplier: s0009, // Pac-Tel
      product: pr0006, // POTS
      descr: `${s0009.descr} ${pr0006.descr}`,
      numItems: 1,
      numOpportunities: 110,
      numOrders: 105,
      numActiveServices: 103,
      seqNum: 1,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0016',
      hrid: 'SP0016',
      supplier: s0002,
      product: pr0036, // DIA BANDWIDTH (Set)
      descr: `${s0002.descr} ${pr0036.descr}`,
      numItems: 3,
      numOpportunities: 140,
      numOrders: 128,
      numActiveServices: 128,
      seqNum: 6,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0017',
      hrid: 'SP0017',
      supplier: s0002,
      product: pr0050, // DIA ACCESS (Set)
      descr: `${s0002.descr} ${pr0050.descr}`,
      numItems: 3,
      numOpportunities: 140,
      numOrders: 128,
      numActiveServices: 128,
      seqNum: 7,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0018',
      hrid: 'SP0018',
      supplier: s0002,
      product: pr0054, // IP ADDRESS BLOCK
      descr: `${s0002.descr} ${pr0054.descr}`,
      // Mirrors XXPR0054: several blocks may be taken together, so options in
      // this Set are added and removed, never swapped. Backs the "Cogent IP
      // Block - /29" row in XXO0003's DIA group, which e2e reads for exactly that.
      doAllowMultipleSelectionsFromSet: true,
      numItems: 3,
      numOpportunities: 140,
      numOrders: 128,
      numActiveServices: 128,
      seqNum: 8,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0019',
      hrid: 'SP0019',
      supplier: s0002,
      product: pr0060, // DIA CPE
      descr: `${s0002.descr} ${pr0060.descr}`,
      numItems: 4,
      numOpportunities: 140,
      numOrders: 128,
      numActiveServices: 128,
      seqNum: 9,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0020',
      hrid: 'SP0020',
      supplier: s0003,
      product: pr0036, // DIA BANDWIDTH (Set)
      descr: `${s0003.descr} ${pr0036.descr}`,
      numItems: 3,
      numOpportunities: 140,
      numOrders: 128,
      numActiveServices: 128,
      seqNum: 2,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0021',
      hrid: 'SP0021',
      supplier: s0003,
      product: pr0050, // DIA ACCESS (Set)
      descr: `${s0003.descr} ${pr0050.descr}`,
      numItems: 3,
      numOpportunities: 140,
      numOrders: 128,
      numActiveServices: 128,
      seqNum: 3,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0022',
      hrid: 'SP0022',
      supplier: s0003,
      product: pr0060, // DIA CPE (Set)
      descr: `${s0003.descr} ${pr0060.descr}`,
      numItems: 3,
      numOpportunities: 140,
      numOrders: 128,
      numActiveServices: 128,
      seqNum: 4,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      // Coeo's four SDWAN box models, as a Set. Modelled on XXSP0019, Cogent's
      // CPE (Set).
      //
      // ⚠ doAllowNoSelectionFromSet is deliberately ABSENT. Its absence is the
      // must-pick-one constraint, which is the whole reason these four moved
      // out of the plain SDWAN kit -- there, nothing stopped a deal taking two
      // boxes or none.
      id: 'XXSP0023',
      hrid: 'SP0023',
      supplier: s0010,
      product: pr0064, // SDWAN Device (Set)
      descr: `${s0010.descr} ${pr0064.descr}`,
      numItems: 4,
      numOpportunities: 23,
      numOrders: 20,
      numActiveServices: 20,
      seqNum: 2,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      // Cogent's routers -- the inner Set of the only Set-inside-a-Set in the
      // mock data (see XXPR0065). Reached from Cogent DIA CPE (Set) through
      // XXSPI0058. doAllowNoSelectionFromSet ABSENT: a deal that takes the
      // managed router must say which one.
      id: 'XXSP0024',
      hrid: 'SP0024',
      supplier: s0002,
      product: pr0065, // Managed Router (Set)
      descr: `${s0002.descr} ${pr0065.descr}`,
      numItems: 2,
      numOpportunities: 0,
      numOrders: 0,
      numActiveServices: 0,
      seqNum: 10,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    // Cogent's Branch Office -- the optional nested plain Kit (XXPR0068). The
    // Wi-Fi Kit is reached from XXSP0025 through XXSPI0062, which is OPTIONAL;
    // the Mounting Kit from XXSP0026 through XXSPI0065, which is not.
    {
      id: 'XXSP0025',
      hrid: 'SP0025',
      supplier: s0002,
      product: pr0068, // Branch Office (Kit)
      descr: `${s0002.descr} ${pr0068.descr}`,
      numItems: 2,
      numOpportunities: 0,
      numOrders: 0,
      numActiveServices: 0,
      seqNum: 11,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0026',
      hrid: 'SP0026',
      supplier: s0002,
      product: pr0069, // Wi-Fi (Kit)
      descr: `${s0002.descr} ${pr0069.descr}`,
      numItems: 3,
      numOpportunities: 0,
      numOrders: 0,
      numActiveServices: 0,
      seqNum: 12,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0027',
      hrid: 'SP0027',
      supplier: s0002,
      product: pr0070, // Wi-Fi Mounting (Kit)
      descr: `${s0002.descr} ${pr0070.descr}`,
      numItems: 1,
      numOpportunities: 0,
      numOrders: 0,
      numActiveServices: 0,
      seqNum: 13,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    // AppDirect's Zscaler DLP - 3 Year (XXPR0074): three slots of the same DLP
    // Kit (XXSPI0067-XXSPI0069), told apart by descr like the E-LAN Hubs.
    {
      id: 'XXSP0028',
      hrid: 'SP0028',
      supplier: s0011,
      product: pr0074, // Zscaler DLP - 3 Year (Kit)
      descr: `${s0011.descr} ${pr0074.descr}`,
      numItems: 3,
      numOpportunities: 0,
      numOrders: 0,
      numActiveServices: 0,
      seqNum: 1,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0029',
      hrid: 'SP0029',
      supplier: s0011,
      product: pr0075, // Data Loss Protection (DLP) (Kit)
      descr: `${s0011.descr} ${pr0075.descr}`,
      numItems: 2,
      numOpportunities: 0,
      numOrders: 0,
      numActiveServices: 0,
      seqNum: 2,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0030',
      hrid: 'SP0030',
      supplier: s0011,
      product: pr0076, // DLP Licensing (Set)
      descr: `${s0011.descr} ${pr0076.descr}`,
      // As production's: none required, any number taken together.
      doAllowNoSelectionFromSet: true,
      doAllowMultipleSelectionsFromSet: true,
      numItems: 1,
      numOpportunities: 0,
      numOrders: 0,
      numActiveServices: 0,
      seqNum: 3,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSP0031',
      hrid: 'SP0031',
      supplier: s0011,
      product: pr0077, // SASE Support & Implementation (Set)
      descr: `${s0011.descr} ${pr0077.descr}`,
      // As production's: none required, any number taken together.
      doAllowNoSelectionFromSet: true,
      doAllowMultipleSelectionsFromSet: true,
      numItems: 4,
      numOpportunities: 0,
      numOrders: 0,
      numActiveServices: 0,
      seqNum: 4,
      status: getSupplierProductStatusById(SupplierProductStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
  ]
}
